// Locates the project ID inside a snippet URL of the shape
// `<origin>/v1/<projectId>/widget.js`. Deliberately loose: the runtime
// treats the ID as an opaque path segment, so any format-tightening
// (length, alphabet, prefix) belongs on the DB side, not here – that way
// generator changes don't require re-shipping every embedded bundle.
const PROJECT_ID_PATTERN = /\/v1\/([^/]+)\/widget\.js(?:$|[?#])/u;

export const extractProjectIdFromUrl = (
	src: string | null | undefined,
): string | null => {
	if (typeof src !== "string" || src === "") {
		return null;
	}
	const match = PROJECT_ID_PATTERN.exec(src);
	return match?.[1] ?? null;
};

const currentHtmlScript = (): HTMLScriptElement | null => {
	const current = document.currentScript;
	return current instanceof HTMLScriptElement ? current : null;
};

// Locates the snippet's own `<script>` by URL shape rather than a specific
// project ID, so the result is stable across `switchProject` (the tag keeps
// the original project's src). Mirrors `extractProjectId`'s currentScript-then-
// scan strategy for the same async-inject reasons.
const findSnippetScript = (): HTMLScriptElement | null => {
	const current = currentHtmlScript();
	if (current !== null && extractProjectIdFromUrl(current.src) !== null) {
		return current;
	}
	for (const script of document.querySelectorAll<HTMLScriptElement>(
		"script[src]",
	)) {
		if (extractProjectIdFromUrl(script.src) !== null) {
			return script;
		}
	}
	return null;
};

// Whether the host page wants the default floating action button. Opt-out
// only: the npm loader stamps `data-launcher="false"` on the injected
// `<script>` when the integrator passes `launcher: false` to `init()`;
// inline-snippet users add the attribute by hand. Read off the snippet's own
// tag, so it resolves synchronously at boot – before the FAB would mount –
// with no dependence on the call queue, which drains only after `bootstrap()`
// returns. Page-global, not per-project, so `switchProject` keeps the choice.
export const readLauncherEnabled = (): boolean =>
	findSnippetScript()?.dataset["launcher"] !== "false";

// Where the live project config is served when the host serves `widget.js`
// from its own domain, to pin a reviewed build. The config holds the
// project's current settings, so it always comes from Weloop. Set with
// `data-config-origin="https://js.weloop.ai"` on the snippet's tag. A
// malformed value is ignored with an error, so the fetch fails visibly
// instead of silently hitting another host.
export const readConfigOrigin = (): string | undefined => {
	const raw = findSnippetScript()?.dataset["configOrigin"];
	if (raw === undefined || raw === "") {
		return undefined;
	}
	try {
		return new URL(raw).origin;
	} catch {
		console.error(`[weloop] invalid data-config-origin "${raw}" – ignored.`);
		return undefined;
	}
};

// Prefers `document.currentScript` (synchronous-load and module-init path),
// falls back to scanning for any `<script>` whose src looks like our snippet –
// covers async-inject quirks (e.g. Safari returning null for currentScript
// after DOMContentLoaded when the npm loader sets `script.async = true`).
export const extractProjectId = (): string | null => {
	const fromCurrent = extractProjectIdFromUrl(currentHtmlScript()?.src);
	if (fromCurrent !== null) {
		return fromCurrent;
	}

	for (const script of document.querySelectorAll<HTMLScriptElement>(
		"script[src]",
	)) {
		const fromScript = extractProjectIdFromUrl(script.src);
		if (fromScript !== null) {
			return fromScript;
		}
	}
	return null;
};

// Returns the per-project config URL derived from the snippet's own script src,
// so dev/preview/prod all resolve cleanly without baking an origin into the
// bundle. The script src is expected to look like `<origin>/v1/<id>/widget.js`;
// we swap the last segment to `config.json`. With a `configOrigin` (a
// self-hosted `widget.js`), the config is read from that origin instead,
// whatever path prefix the host serves the script under.
export const deriveConfigUrl = (
	scriptSrc: string,
	configOrigin?: string,
): string => {
	// `script.src` is always absolute when read from a DOM `<script>` element,
	// so no base is required. Replacing the last segment keeps the version
	// prefix and per-project ID intact: the Edge Rule rewrites
	// `/v1/*/widget.js` to the canonical bundle, but `config.json` is served
	// per-project from storage and isn't rewritten.
	const projectId = extractProjectIdFromUrl(scriptSrc);
	if (configOrigin !== undefined && projectId !== null) {
		return new URL(
			`/v1/${encodeURIComponent(projectId)}/config.json`,
			configOrigin,
		).toString();
	}
	const url = new URL(scriptSrc);
	url.pathname = url.pathname.replace(/\/widget\.js$/u, "/config.json");
	url.search = "";
	return url.toString();
};

// Sibling of `widget.js` that ships the rrweb UMD. Loaded on demand via a
// `<script>` tag when session replay is enabled – keeping the main bundle
// rrweb-free (its size budget is much tighter than rrweb's footprint).
export const deriveReplayBundleUrl = (scriptSrc: string): string => {
	const url = new URL(scriptSrc);
	url.pathname = url.pathname.replace(/\/widget\.js$/u, "/replay.js");
	url.search = "";
	return url.toString();
};

// Sibling of `config.json` that ships the rrweb UMD. Fallback for when the
// snippet's own `<script>` tag cannot be located by project id – after
// `switchProject` the tag keeps the original project's src, so a lookup by
// the new id finds nothing and recordings would be refused on every start.
export const deriveReplayBundleUrlFromConfigUrl = (
	configUrl: string,
): string => {
	const url = new URL(configUrl);
	url.pathname = url.pathname.replace(/\/config\.json$/u, "/replay.js");
	url.search = "";
	return url.toString();
};

// Rebuilds a config URL for a different project by swapping the projectId
// segment in `/v1/<id>/config.json`. Used by `switchProject` so we don't have
// to re-derive from the (possibly stale) `<script>` tag every time.
export const rebuildConfigUrl = (
	templateUrl: string,
	projectId: string,
): string => {
	const url = new URL(templateUrl);
	url.pathname = url.pathname.replace(
		/\/v1\/[^/]+\/config\.json$/u,
		`/v1/${projectId}/config.json`,
	);
	return url.toString();
};

export const findScriptSrc = (projectId: string): string | null => {
	const current = currentHtmlScript();
	if (current?.src.includes(projectId) === true) {
		return current.src;
	}
	for (const script of document.querySelectorAll<HTMLScriptElement>(
		"script[src]",
	)) {
		if (script.src.includes(projectId)) {
			return script.src;
		}
	}
	return null;
};
